import { CampaignActivityEventType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import type { CampaignActivityRecord } from 'noddi-async';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiCard, NoddiSpinner, usePaginationQueryParam } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

type Props = {
  campaignId: number;
};

const EVENT_META: Record<CampaignActivityEventType, { icon: NoddiIconName; phrase: string }> = {
  [CampaignActivityEventType.CREATED]: { icon: 'Plus', phrase: 'created the campaign' },
  [CampaignActivityEventType.NAME_CHANGED]: { icon: 'Edit', phrase: 'updated the name' },
  [CampaignActivityEventType.DESCRIPTION_CHANGED]: { icon: 'FileText', phrase: 'updated the description' },
  [CampaignActivityEventType.AUDIENCE_CHANGED]: { icon: 'Users', phrase: 'updated the audience' },
  [CampaignActivityEventType.SCOPE_CHANGED]: { icon: 'MapPin', phrase: 'updated the scope' },
  [CampaignActivityEventType.CLAIM_DEADLINE_CHANGED]: { icon: 'Calendar', phrase: 'updated the claim deadline' },
  [CampaignActivityEventType.EXPIRES_AFTER_CLAIM_CHANGED]: {
    icon: 'Clock',
    phrase: 'updated expires after claim'
  },
  [CampaignActivityEventType.DISCOUNTS_CHANGED]: { icon: 'Tag', phrase: 'updated discounts' },
  [CampaignActivityEventType.CUSTOMER_ASSIGNED]: { icon: 'UserPlus', phrase: 'assigned a customer' },
  [CampaignActivityEventType.CUSTOMER_ACTIVATED]: {
    icon: 'UserPlus',
    phrase: 'a customer activated the campaign'
  },
  [CampaignActivityEventType.DELETED]: { icon: 'Trash2', phrase: 'deleted the campaign' }
};

export const CampaignActivityTab = ({ campaignId }: Props) => {
  const { pagination } = usePaginationQueryParam();

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedCampaignActivities,
    input: { campaignId, ...pagination },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (isPending) {
    return (
      <div className='flex items-center justify-center py-10'>
        <NoddiSpinner />
      </div>
    );
  }

  const activities = data?.results ?? [];

  return (
    <NoddiCard title='Activity' size='small' icon='Activity' iconTile>
      {activities.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No activity yet.</p>
      ) : (
        <div className='flex flex-col gap-4'>
          {activities.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </NoddiCard>
  );
};

const ActivityRow = ({ activity }: { activity: CampaignActivityRecord }) => {
  const actorName = activity.actorName ?? 'System';
  const timestamp = noddiFormatDate(activity.createdAt, NoddiDateFormats.DATE_TIME);
  const eventType = activity.eventType.value;
  const meta = EVENT_META[eventType];
  const icon = meta?.icon ?? 'Circle';
  const phrase = meta?.phrase ?? eventType.toLowerCase().replaceAll('_', ' ');
  const detail = describeActivityDetail(activity);

  return (
    <div className='flex items-start gap-3'>
      <div className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted')}>
        <NoddiIcon name={icon} size='small' className='text-muted-foreground' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm'>
          <span className='font-medium'>{actorName}</span>
          <span className='text-muted-foreground'>{phrase}</span>
          <span className='ml-auto text-xs text-muted-foreground'>{timestamp}</span>
        </div>
        {detail && <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{detail}</p>}
      </div>
    </div>
  );
};

const describeActivityDetail = (activity: CampaignActivityRecord): string | null => {
  if (activity.detail?.trim()) {
    return activity.detail.trim();
  }

  switch (activity.eventType.value) {
    case CampaignActivityEventType.NAME_CHANGED:
      return changeLine(activity.nameFrom, activity.nameTo);
    case CampaignActivityEventType.DESCRIPTION_CHANGED:
      return changeLine(activity.descriptionFrom, activity.descriptionTo);
    case CampaignActivityEventType.AUDIENCE_CHANGED:
      return boolChangeLine('New users only', activity.isForNewUserOnlyFrom, activity.isForNewUserOnlyTo);
    case CampaignActivityEventType.SCOPE_CHANGED:
      return boolChangeLine('Organization-wide', activity.isOrganizationWideFrom, activity.isOrganizationWideTo);
    case CampaignActivityEventType.CLAIM_DEADLINE_CHANGED:
      return changeLine(
        activity.expiresAtFrom ? noddiFormatDate(activity.expiresAtFrom, NoddiDateFormats.DATE_TIME) : undefined,
        activity.expiresAtTo ? noddiFormatDate(activity.expiresAtTo, NoddiDateFormats.DATE_TIME) : undefined
      );
    case CampaignActivityEventType.EXPIRES_AFTER_CLAIM_CHANGED:
      return changeLine(
        activity.expiryDaysAfterReceivingFrom?.toString(),
        activity.expiryDaysAfterReceivingTo?.toString()
      );
    case CampaignActivityEventType.CUSTOMER_ASSIGNED:
    case CampaignActivityEventType.CUSTOMER_ACTIVATED:
      return activity.userGroupIdSnapshot != null ? `Customer #${activity.userGroupIdSnapshot}` : null;
    default:
      return null;
  }
};

const changeLine = (from?: string | null, to?: string | null): string | null => {
  const fromValue = from?.trim() || '-';
  const toValue = to?.trim() || '-';
  if (fromValue === '-' && toValue === '-') {
    return null;
  }
  return `${fromValue} -> ${toValue}`;
};

const boolChangeLine = (label: string, from?: boolean | null, to?: boolean | null): string | null => {
  if (from == null && to == null) {
    return null;
  }
  return `${label}: ${formatBool(from)} -> ${formatBool(to)}`;
};

const formatBool = (value?: boolean | null): string => {
  if (value == null) {
    return '-';
  }
  return value ? 'Yes' : 'No';
};
